import { useEffect, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { isAdminTier, isManagerTier } from "../utils/capabilities";
import BackButton from "../components/BackButton";

const SCOPE_LABEL = {
  building: "Building",
  job: "Job",
  account: "Account",
};

/**
 * Conversation view for a single thread. Subscribes to the messages
 * subcollection in real time; sends new messages via the sendMessage action
 * (which also bumps lastMessage* fields on the parent so the inbox stays
 * fresh).
 */
export default function MessageThreadPage({
  thread,
  actions,
  orgId,
  user,
  role,
  onBack,
}) {
  // Rules let admin/manager-tier members of the thread delete it.
  const canDelete = isAdminTier(role) || isManagerTier(role);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!thread?.id) return;
    const q = query(
      collection(db, `messageThreads/${thread.id}/messages`),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) =>
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("messages listener:", err.message)
    );
    return unsub;
  }, [thread?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    await actions.sendMessage(thread.id, body);
    setBody("");
    setSending(false);
  };

  const [deleting, setDeleting] = useState(false);
  const deleteThread = async () => {
    const ok = window.confirm(
      `Delete this entire thread?\n\n"${thread.subject || "(no subject)"}"\n\nAll messages will be permanently removed. This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await actions.deleteMessageThread(thread.id);
      onBack();
    } catch (err) {
      setDeleting(false);
      window.alert(`Couldn't delete: ${err.message}`);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      {canDelete && (
        <button
          style={{ ...styles.button, color: "red" }}
          onClick={deleteThread}
          disabled={deleting}
        >
          {deleting ? "Deleting..." : "Delete Thread"}
        </button>
      )}
      <h2>{thread.subject || "(no subject)"}</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        <span
          style={{
            background: "#e0e0e0",
            borderRadius: "4px",
            padding: "1px 6px",
            marginRight: "6px",
          }}
        >
          {SCOPE_LABEL[thread.scope] || thread.scope}
        </span>
        {thread.scope === "direct"
          ? (() => {
              const names = thread.participantNames || [];
              const emails = thread.participantEmails || [];
              const idx = (thread.participants || []).findIndex(
                (u) => u !== user?.uid
              );
              if (idx >= 0)
                return names[idx] || emails[idx] || "Direct message";
              return "Direct message";
            })()
          : thread.scopeLabel || ""}
      </p>

      <div
        ref={scrollRef}
        style={{
          border: "1px solid #ddd",
          borderRadius: "6px",
          padding: "10px",
          maxHeight: "400px",
          overflowY: "auto",
          background: "#fafafa",
        }}
      >
        {messages.length === 0 ? (
          <p style={{ fontSize: "12px", color: "#666" }}>No messages yet.</p>
        ) : (
          messages.map((m) => {
            const mine = m.senderOrgId === orgId && m.senderUid === user?.uid;
            const ours = m.senderOrgId === orgId;
            return (
              <div
                key={m.id}
                style={{
                  marginBottom: "8px",
                  textAlign: mine ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    maxWidth: "75%",
                    padding: "6px 10px",
                    borderRadius: "10px",
                    background: mine ? "#cfe5ff" : ours ? "#e8f0ff" : "white",
                    border: "1px solid #ccc",
                    textAlign: "left",
                    fontSize: "13px",
                  }}
                >
                  <div
                    style={{ fontSize: "11px", color: "#666", marginBottom: "2px" }}
                  >
                    {m.senderName || m.senderEmail || "unknown"}
                    {m.createdAt
                      ? ` • ${new Date(m.createdAt).toLocaleString()}`
                      : ""}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <textarea
        style={{ ...styles.textarea, marginTop: "10px" }}
        placeholder="Type a message..."
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
      />
      <button
        style={styles.button}
        onClick={send}
        disabled={sending || !body.trim()}
      >
        {sending ? "Sending..." : "Send"}
      </button>
    </>
  );
}
